import { useMemo, useState } from "react";
import {
  Box,
  Tab,
  Tabs,
  Tooltip,
  CircularProgress,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import {
  Button,
  ConfirmationModal,
  DrawerContent,
  DrawerCustom,
  DrawerNavBar,
  Typography,
} from "@platform/design-system";
import { useNotification } from "@platform/hooks";

import { ProposalsProvider } from "./contexts/proposalsContext";
import { UploadFilesProvider } from "./contexts/uploadFilesContext";
import { useUploadFiles } from "./hooks";
import { useUploadDocuments } from "./hooks/useDocumentsApi";
import { useProposals } from "./hooks/useProposals";
import { HistoryTab, UploadTab } from "./tabs";

import type { Carrier } from "./types";

export interface UploadDocumentsDrawerProps {
  open: boolean;
  carriers: Carrier[];
  ecoRfpId: string;
  brokerId: number | undefined;
  brokerPortalRfpId: number | undefined;
  onClose: VoidFunction;
}

type DrawerTab = "upload" | "history";

function UploadDrawerContent({
  carriers,
  ecoRfpId,
  brokerId,
  brokerPortalRfpId,
  open,
  onClose,
}: UploadDocumentsDrawerProps): React.JSX.Element {
  const [activeTab, setActiveTab] = useState<DrawerTab>("upload");
  const [selectedCarrier, setSelectedCarrier] = useState<string | null>(null);
  const { files, clearFiles } = useUploadFiles();
  const {
    proposals,
    loading: proposalsLoading,
    error: proposalsError,
    hasTimeout,
    clearTimeout,
  } = useProposals();
  const { showSuccess } = useNotification();
  const { uploadDocuments, loading, error, clearError } = useUploadDocuments({
    ecoRfpId,
    brokerId,
    brokerPortalRfpId,
    onSuccess: () => {
      showSuccess("Documents uploaded successfully");
      clearFiles();
      setSelectedCarrier(null);
    },
  });

  const totalDocuments = proposals.reduce(
    (acc, proposal) => acc + proposal.documents.length,
    0,
  );

  // The picker works in display names; resolve the selection to the carrier's
  // canonical shareIdentifier (sent to the backend as carrierIdentifier).
  const carrierIdByName = useMemo(
    () => new Map(carriers.map((carrier) => [carrier.name, carrier.id])),
    [carriers],
  );
  const resolvedCarrierId = selectedCarrier
    ? (carrierIdByName.get(selectedCarrier) ?? null)
    : null;

  const hasFiles = files.length > 0;
  const canSubmit = hasFiles && resolvedCarrierId !== null && !loading;

  const getSubmitTooltip = (): string => {
    if (!hasFiles) return "Upload documents to submit";
    if (!selectedCarrier) return "Select a carrier to submit";
    if (!resolvedCarrierId)
      return "Select a known carrier from the list to submit";
    return "";
  };

  const handleSubmit = (): void => {
    if (!resolvedCarrierId) return;
    uploadDocuments(
      resolvedCarrierId,
      files.map((f) => f.file),
    );
  };

  const modalDescription =
    error ?? (hasTimeout ? "AI Processing time-out, try again later" : "");

  const handleModalClose = (): void => {
    clearError();
    clearTimeout();
  };

  return (
    <>
      <DrawerCustom anchor="right" size="lg" open={open} onClose={onClose}>
        <DrawerNavBar>
          <Box
            justifyContent="space-between"
            display="flex"
            alignItems="center"
            width="100%"
          >
            <Typography variant="subtitle1" color="textSecondary">
              Quote Documents
            </Typography>
          </Box>
          <IconButton aria-label="Close drawer" onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </DrawerNavBar>
        <DrawerContent>
          <Tabs
            value={activeTab}
            onChange={(_, val: DrawerTab) => setActiveTab(val)}
            textColor="secondary"
            indicatorColor="secondary"
            aria-label="Quote documents tabs"
            sx={{
              borderBottom: 1,
              borderColor: "divider",
              "& .MuiTab-root.Mui-selected": { color: "brandPrimary.main" },
              "& .MuiTabs-indicator": { backgroundColor: "brandPrimary.main" },
            }}
          >
            <Tab label="Upload & Process" value="upload" />
            <Tab
              label={
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  History
                  {totalDocuments > 0 && (
                    <Box
                      sx={{
                        backgroundColor: "grey.300",
                        borderRadius: "50px",
                        minWidth: 20,
                        height: 20,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        px: "4px",
                        fontSize: "0.75rem",
                        fontWeight: 500, // Medium
                        color: "grey.600",
                        lineHeight: "20px",
                        letterSpacing: "0.14px",
                      }}
                    >
                      {totalDocuments}
                    </Box>
                  )}
                </Box>
              }
              value="history"
            />
          </Tabs>

          {activeTab === "upload" && (
            <UploadTab
              carriers={carriers}
              selectedCarrier={selectedCarrier}
              onCarrierChange={setSelectedCarrier}
              isSubmitting={loading}
            />
          )}
          {activeTab === "history" && (
            <HistoryTab
              proposals={proposals}
              loading={proposalsLoading}
              error={proposalsError}
              ecoRfpId={ecoRfpId}
            />
          )}
        </DrawerContent>
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            gap: 2,
            p: 2,
            borderTop: 1,
            borderColor: "divider",
          }}
        >
          <Button variant="tertiary" onClick={onClose}>
            Cancel
          </Button>
          {activeTab === "upload" && (
            <Tooltip title={getSubmitTooltip()} placement="top">
              <span>
                <Button
                  variant="primary"
                  onClick={handleSubmit}
                  disabled={!canSubmit}
                  startIcon={
                    loading ? (
                      <CircularProgress size={14} color="inherit" />
                    ) : null
                  }
                >
                  {loading ? "Submitting..." : "Submit"}
                </Button>
              </span>
            </Tooltip>
          )}
        </Box>
      </DrawerCustom>
      {(error || hasTimeout) && (
        <ConfirmationModal
          open={true}
          title="Failed to upload Proposal"
          description={modalDescription}
          confirmButtonText={error ? "Retry" : "Acknowledge"}
          onAccept={error ? handleSubmit : handleModalClose}
          onCancel={handleModalClose}
          cancelButtonText={error ? "Cancel" : "Close"}
        />
      )}
    </>
  );
}

export function UploadDocumentsDrawer({
  carriers,
  ecoRfpId,
  brokerId,
  brokerPortalRfpId,
  open,
  onClose,
}: UploadDocumentsDrawerProps): React.JSX.Element {
  return (
    <ProposalsProvider rfpId={ecoRfpId} carriers={carriers}>
      <UploadFilesProvider>
        <UploadDrawerContent
          open={open}
          carriers={carriers}
          ecoRfpId={ecoRfpId}
          brokerId={brokerId}
          brokerPortalRfpId={brokerPortalRfpId}
          onClose={onClose}
        />
      </UploadFilesProvider>
    </ProposalsProvider>
  );
}
